import React, { useEffect, useState } from 'react';
import RenderComponent from './render-component';
import { Box, Typography, Input, TextField, Divider } from '@material-ui/core';
import { Button, SaveButton, TextInput, useNotify } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import UploadImageItem from './components/widget-image-item';

const useStyles = makeStyles({
  root: {
    width: '40%',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    marginRight: '30px',
  },
});

const defaultValues = {
  text: '',
  fontSize: 14,
  letterSpacing: 0.6,
  images: [],
};

interface Image {
  source: string | null;
  preview: string | null;
}

const EditTimelineRotationLogoText = (props: any) => {
  const widgetImages = props.widget.data.images || defaultValues.images;

  const [text, setText] = useState(defaultValues.text);
  const [letterSpacing, setLetterSpacing] = useState(defaultValues.letterSpacing);
  const [fontSize, setFontSize] = useState(defaultValues.fontSize);
  const [images, setImages] = useState<Image[]>(
    widgetImages.map((image) => ({
      source: image,
      preview: createFullLink(image),
    }))
  );

  const [previewImage, setPreviewImage] = useState(images[0]?.preview || null);
  const classes = useStyles();
  const notify = useNotify();

  useEffect(() => {
    if (props.widget) {
      setText(props.widget.data.text || defaultValues.text);
      setLetterSpacing(props.widget.data.letterSpacing || defaultValues.letterSpacing);
      setFontSize(props.widget.data.fontSize || defaultValues.fontSize);
      setImages(
        widgetImages.map((image) => ({
          source: image,
          preview: createFullLink(image),
        }))
      );
      setPreviewImage(widgetImages.length ? widgetImages[0] : null);
    }
  }, [props.widget]);

  const handleChangeImage = (encodedImage: string | null, previewImage: string | null, index: number) => {
    setImages(
      images.map<Image>((item, i) => {
        return i === index ? { source: encodedImage, preview: previewImage } : item;
      })
    );
    setPreviewImage(previewImage);
  };
  const handleUploadFileError = (message: string) => {
    notify(message);
  };
  const handlePreview = (image: string) => {
    setPreviewImage(image);
  };
  const handleAddImage = () => {
    setImages([...images, { source: null, preview: null }]);
  };
  const handleDelete = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    setPreviewImage(newImages[0]?.preview || null);
  };
  const handleSubmit = () => {
    props.onSubmit({
      text,
      letterSpacing: letterSpacing,
      fontSize: fontSize,
      images: images.map(({ source }) => source).filter(Boolean),
    });
  };

  const steps = Array.from({ length: images.length }).map((_, i) => `Step ${i + 1}`);

  return (
    <div>
      <Box display={'flex'} alignItems={'center'}>
        <Box className={classes.root}>
          <TextField
            label='Logo Text'
            variant='outlined'
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
          />
          <Divider />
          <TextField
            label='Font Size'
            variant='outlined'
            inputProps={{ step: '0.1', type: 'number' }}
            value={fontSize}
            onChange={(event) => {
              setFontSize(Number(event.target.value));
            }}
          />
          <Divider />
          <TextField
            label='Letter Spacing'
            variant='outlined'
            inputProps={{ step: '0.01', type: 'number' }}
            value={letterSpacing}
            onChange={(event) => {
              setLetterSpacing(Number(event.target.value));
            }}
          />
          {Boolean(steps.length) && (
            <React.Fragment>
              <Divider />
              {steps.map((step, index) => (
                <UploadImageItem
                  key={index}
                  title={step}
                  image={images[index].preview}
                  onChange={({ encodedImage, previewImage }) => handleChangeImage(encodedImage, previewImage, index)}
                  onError={handleUploadFileError}
                  onPreview={handlePreview}
                  onDelete={() => handleDelete(index)}
                />
              ))}
            </React.Fragment>
          )}
          <Divider />
          <Box display='flex'>
            <Button onClick={handleAddImage} className='cancel-button' label='Add image' />
          </Box>
        </Box>
        <Box width={'60%'}>
          <RenderComponent
            widgetType={'timeline-logo-rotation-text'}
            widgetData={{ text, letterSpacing, fontSize, image: previewImage }}
          />
        </Box>
      </Box>
      <Box marginTop={'20px'} marginBottom={'30px'}>
        <Button onClick={handleSubmit} className='cancel-button' label='Save' />
        <Button onClick={props.onCancel} className='cancel-button' label='Cancel' />
      </Box>
    </div>
  );
};

function createFullLink(imageLink) {
  let AWSS3Url = process.env.REACT_APP_AWS_S3_URL || '';
  AWSS3Url = AWSS3Url.replace(/\/$/, '');
  return `${AWSS3Url}/${imageLink}`;
}

export default EditTimelineRotationLogoText;
